import React, { useState, useEffect, useRef } from "react";

const STORAGE_KEY = "infinium_brands";

const loadBrands = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : [];
  } catch {
    return [];
  }
};

const saveBrands = (brands) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(brands));
  } catch {}
};

const BrandModal = ({ isDarkMode, brand, onSave, onClose }) => {
  const [name, setName] = useState(brand?.name || "");
  const [primaryColor, setPrimaryColor] = useState(brand?.primaryColor || "#3b82f6");
  const [secondaryColor, setSecondaryColor] = useState(brand?.secondaryColor || "#6b7280");
  const [prompt, setPrompt] = useState(brand?.prompt || "");
  const [logo, setLogo] = useState(brand?.logo || null);
  const logoInputRef = useRef(null);

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setLogo(ev.target.result);
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSave({
      id: brand?.id || `brand-${Date.now()}`,
      name: name.trim(),
      logo,
      primaryColor,
      secondaryColor,
      prompt: prompt.trim(),
    });
  };

  const bg = isDarkMode ? "bg-gray-900" : "bg-white";
  const border = isDarkMode ? "border-gray-700" : "border-gray-200";
  const inputBg = isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900";
  const labelClass = `block text-xs font-semibold mb-1.5 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`;
  const textPrimary = isDarkMode ? "text-white" : "text-gray-900";

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className={`relative w-full max-w-lg rounded-2xl shadow-2xl border p-6 ${bg} ${border}`}>
        <button
          onClick={onClose}
          className={`absolute top-4 right-4 p-1.5 rounded-full transition-colors ${isDarkMode ? "text-gray-400 hover:text-white hover:bg-gray-700" : "text-gray-400 hover:text-gray-700 hover:bg-gray-100"}`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <h2 className={`text-lg font-bold mb-5 ${textPrimary}`}>
          {brand ? "Edit Brand" : "Add New Brand"}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Brand Name */}
          <div>
            <label className={labelClass}>Brand Name <span className="text-red-500">*</span></label>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Acme Corp"
              className={`w-full px-3 py-2 text-sm rounded-xl border outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 transition-all ${inputBg}`}
            />
          </div>

          {/* Logo Upload */}
          <div>
            <label className={labelClass}>Brand Logo</label>
            <div className="flex items-center gap-3">
              <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoChange} />
              <button
                type="button"
                onClick={() => logoInputRef.current?.click()}
                className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm transition-all ${
                  logo
                    ? "border-emerald-500 text-emerald-500 bg-emerald-500/10"
                    : isDarkMode
                      ? "border-gray-600 text-gray-400 hover:border-gray-400"
                      : "border-gray-300 text-gray-500 hover:border-gray-500"
                }`}
              >
                {logo ? (
                  <img src={logo} alt="logo" className="w-5 h-5 rounded object-cover" />
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                )}
                {logo ? "Change Logo" : "Upload Logo"}
              </button>
              {logo && (
                <button
                  type="button"
                  onClick={() => setLogo(null)}
                  className="text-xs text-red-400 hover:text-red-500 transition-colors"
                >
                  Remove
                </button>
              )}
            </div>
          </div>

          {/* Colors */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Primary Color</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="w-10 h-10 rounded-lg border-0 cursor-pointer bg-transparent"
                />
                <input
                  type="text"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  placeholder="#3b82f6"
                  className={`flex-1 px-3 py-2 text-sm rounded-xl border outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 transition-all font-mono ${inputBg}`}
                />
              </div>
            </div>
            <div>
              <label className={labelClass}>Secondary Color</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={secondaryColor}
                  onChange={(e) => setSecondaryColor(e.target.value)}
                  className="w-10 h-10 rounded-lg border-0 cursor-pointer bg-transparent"
                />
                <input
                  type="text"
                  value={secondaryColor}
                  onChange={(e) => setSecondaryColor(e.target.value)}
                  placeholder="#6b7280"
                  className={`flex-1 px-3 py-2 text-sm rounded-xl border outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 transition-all font-mono ${inputBg}`}
                />
              </div>
            </div>
          </div>

          {/* Common Prompt */}
          <div>
            <label className={labelClass}>Common Post Prompt</label>
            <textarea
              rows="3"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="e.g., Always use a friendly tone. Highlight our eco-friendly values. Include a call to action."
              className={`w-full px-3 py-2 text-sm rounded-xl border resize-none outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 transition-all ${inputBg}`}
            />
            <p className={`text-xs mt-1 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
              This prompt will be included with every post generated for this brand.
            </p>
          </div>

          <div className="flex gap-2 pt-1">
            <button
              type="submit"
              className="flex-1 px-4 py-2.5 rounded-xl text-white text-sm font-bold transition-all hover:brightness-105 active:scale-[0.98]"
              style={{ background: "linear-gradient(135deg, #22d3ee, #60a5fa)" }}
            >
              {brand ? "Save Changes" : "Add Brand"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all active:scale-[0.98] border ${isDarkMode ? "bg-gray-800 text-gray-300 border-gray-700 hover:bg-gray-700" : "bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-200"}`}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const BrandCard = ({ brand, isDarkMode, onEdit, onDelete }) => {
  const cardBg = isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200";
  const textPrimary = isDarkMode ? "text-white" : "text-gray-900";
  const textMuted = isDarkMode ? "text-gray-400" : "text-gray-500";

  return (
    <div className={`rounded-2xl border p-4 shadow-sm transition-all hover:shadow-md ${cardBg}`}>
      <div className="flex items-start gap-3">
        {/* Logo or fallback */}
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg flex-shrink-0 overflow-hidden"
          style={{ background: brand.primaryColor || "#3b82f6" }}
        >
          {brand.logo ? (
            <img src={brand.logo} alt={brand.name} className="w-full h-full object-cover" />
          ) : (
            brand.name.charAt(0).toUpperCase()
          )}
        </div>

        <div className="flex-1 min-w-0">
          <h3 className={`font-bold text-base truncate ${textPrimary}`}>{brand.name}</h3>

          {/* Color swatches */}
          <div className="flex items-center gap-2 mt-1.5">
            <div className="flex items-center gap-1.5">
              <div
                className="w-4 h-4 rounded-full border border-white/20 shadow-sm"
                style={{ backgroundColor: brand.primaryColor }}
                title={`Primary: ${brand.primaryColor}`}
              />
              <div
                className="w-4 h-4 rounded-full border border-white/20 shadow-sm"
                style={{ backgroundColor: brand.secondaryColor }}
                title={`Secondary: ${brand.secondaryColor}`}
              />
            </div>
            <span className={`text-xs font-mono ${textMuted}`}>
              {brand.primaryColor} · {brand.secondaryColor}
            </span>
          </div>

          {brand.prompt && (
            <p className={`text-xs mt-1.5 line-clamp-2 ${textMuted}`}>{brand.prompt}</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-1.5 flex-shrink-0">
          <button
            onClick={() => onEdit(brand)}
            className={`p-1.5 rounded-lg transition-colors ${isDarkMode ? "text-gray-400 hover:text-white hover:bg-gray-700" : "text-gray-400 hover:text-gray-700 hover:bg-gray-100"}`}
            title="Edit brand"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            onClick={() => onDelete(brand.id)}
            className={`p-1.5 rounded-lg transition-colors ${isDarkMode ? "text-gray-500 hover:text-red-400 hover:bg-gray-700" : "text-gray-400 hover:text-red-500 hover:bg-gray-100"}`}
            title="Delete brand"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

const BrandManagement = ({ isDarkMode, onBrandsChange }) => {
  const [brands, setBrands] = useState(loadBrands);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);

  const persistAndNotify = (updated) => {
    saveBrands(updated);
    if (onBrandsChange) onBrandsChange(updated);
    return updated;
  };

  const handleSave = (brandData) => {
    setBrands((prev) => {
      const existing = prev.findIndex((b) => b.id === brandData.id);
      const updated = existing >= 0
        ? prev.map((b) => (b.id === brandData.id ? brandData : b))
        : [...prev, brandData];
      persistAndNotify(updated);
      return updated;
    });
    setModalOpen(false);
    setEditingBrand(null);
  };

  const handleEdit = (brand) => {
    setEditingBrand(brand);
    setModalOpen(true);
  };

  const handleDelete = (id) => {
    if (!window.confirm("Delete this brand?")) return;
    setBrands((prev) => {
      const updated = prev.filter((b) => b.id !== id);
      persistAndNotify(updated);
      return updated;
    });
  };

  const handleClose = () => {
    setModalOpen(false);
    setEditingBrand(null);
  };

  const pageBg = isDarkMode ? "bg-gray-950 text-white" : "bg-gray-50 text-gray-900";
  const textMuted = isDarkMode ? "text-gray-400" : "text-gray-500";

  return (
    <div className={`flex-1 overflow-y-auto ${pageBg}`}>
      <div className="max-w-3xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold">Brand Kit</h1>
            <p className={`text-sm mt-1 ${textMuted}`}>
              Manage your brands. The selected brand's logo, colors, and prompt will be used when generating social posts.
            </p>
          </div>
          <button
            onClick={() => { setEditingBrand(null); setModalOpen(true); }}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-white text-sm font-bold transition-all hover:brightness-105 active:scale-[0.98] flex-shrink-0"
            style={{ background: "linear-gradient(135deg, #22d3ee, #60a5fa)" }}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
            Add Brand
          </button>
        </div>

        {/* Brand List */}
        {brands.length === 0 ? (
          <div className={`rounded-2xl border-2 border-dashed p-12 flex flex-col items-center justify-center text-center ${isDarkMode ? "border-gray-700" : "border-gray-300"}`}>
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-4 ${isDarkMode ? "bg-gray-800" : "bg-gray-100"}`}>
              <svg className={`w-7 h-7 ${textMuted}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
            </div>
            <h3 className="font-bold text-base mb-1">No brands yet</h3>
            <p className={`text-sm ${textMuted} mb-4`}>Add your first brand to get started with branded post generation.</p>
            <button
              onClick={() => { setEditingBrand(null); setModalOpen(true); }}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-white text-sm font-semibold transition-all hover:brightness-105"
              style={{ background: "linear-gradient(135deg, #22d3ee, #60a5fa)" }}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
              </svg>
              Add Brand
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {brands.map((brand) => (
              <BrandCard
                key={brand.id}
                brand={brand}
                isDarkMode={isDarkMode}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>

      {modalOpen && (
        <BrandModal
          isDarkMode={isDarkMode}
          brand={editingBrand}
          onSave={handleSave}
          onClose={handleClose}
        />
      )}
    </div>
  );
};

export default BrandManagement;
export { loadBrands };
